import { Component, Input, ViewChild } from "@angular/core";
import { MatTree, MatTreeNestedDataSource } from "@angular/material/tree";
import { ThesaurusEntry } from "@interfaces/thesaurus-entry.interface";
import { Thesaurus } from "@interfaces/thesaurus.interface";
import { ThesaurusService } from "@thesaurus-service/thesaurus.service";
import { firstValueFrom } from "rxjs";
import { ThesaurusPath } from "@interfaces/thesaurus-path.interface";
import { t } from "i18next";
import { EventsService } from "app/services/events/events.service";
import { NgxSpinnerService } from "ngx-spinner";
import { UtilitiesService } from "@utilities-service/utilities.service";

@Component({
    selector: 'app-thesaurus-hierarchical-view',
    templateUrl: './thesaurus-hierarchical-view.component.html',
    styleUrls: ['./thesaurus-hierarchical-view.component.scss'],
    standalone: false
})
  
export class ThesaurusHierarchicalViewComponent {
    @Input() thesaurus?: Thesaurus;
    @Input() token!: string;
//    @Input() thesaurusId: string = '';
    @Input() entryOrdinal: number = 0;
    @Input() thesauri?: Thesaurus[];
    @Input() major!: boolean;
    @Input() explode!: boolean;

    entry!: ThesaurusEntry;
    rootNode!: ThesaurusEntry;
    thesaurusDataSource: MatTreeNestedDataSource<ThesaurusEntry>;
    childrenAccessor = (node: ThesaurusEntry) => node.narrowerTerms ?? [];
    @ViewChild('tree')
    thesTree!: MatTree<ThesaurusEntry>;
    isUseForTerm: boolean = false;
    useTermsInstead!: ThesaurusEntry;
    hasNotes: boolean = false;
    entryObjects: ThesaurusEntry[] = [];
 
    constructor(private thesaurusService: ThesaurusService, 
                private eventsService: EventsService, 
                private spinnerService: NgxSpinnerService,
                public utilitiesService: UtilitiesService) {
        this.thesaurusDataSource = new MatTreeNestedDataSource<ThesaurusEntry>();
    }

    /** Lifecycle hook: initialises the hierarchical tree and expands/collapses nodes based on the entry type. */
    async ngOnInit(): Promise<void> {
        this.showIndexSpinner();
        await this.buildInitialTree(this.thesaurus ? this.thesaurus.id : '', this.entryOrdinal, this.token).then(
            (rootNode) => {
                this.rootNode = rootNode;
                this.thesaurusDataSource.data = [this.rootNode];
                this.thesTree.expandDescendants(this.rootNode);
                if (this.isUseForTerm) {
                    if (this.entryObjects.length > 0) {
                        this.entryObjects.forEach(entryObj => {
                            this.thesTree.collapseDescendants(entryObj);
                        });
                    }
                    else {
                        this.thesTree.collapseDescendants(this.useTermsInstead);
                    }
                }
                else {
                    if (this.entryObjects.length > 0) {
                        this.entryObjects.forEach(entryObj => {
                            this.thesTree.collapseDescendants(entryObj);
                        });
                    }
                    else {
                        this.thesTree.collapseDescendants(this.entry);
                    }
                }
            }
        ).finally(() => {
            this.hideIndexSpinner();
        });        
    }

    /**
     * Fetches the thesaurus root and the target entry, then walks every path from the
     * root to that entry, inserting each intermediate term into the tree.
     * @returns The populated root node ready to be bound to the data source.
     */
    async buildInitialTree(thesaurusId: string, entryOrdinal: number, token: string): Promise<ThesaurusEntry> {
        this.rootNode = await firstValueFrom(this.thesaurusService.getthesaurusRoot(thesaurusId, token));
        this.rootNode.name = "Root of " + this.thesaurus?.name
        this.entry = await firstValueFrom(this.thesaurusService.getthesaurusSingleEntry(thesaurusId, entryOrdinal, token));
        this.hasNotes = this.checkIfEntryHasNotes(this.entry);
        if (this.entry.useTermsInstead) {
            this.isUseForTerm = true;
            this.useTermsInstead = await firstValueFrom(this.thesaurusService.getthesaurusSingleEntry(thesaurusId, this.entry.useTermsInstead[0].termOrdinal, token));
        }
        this.rootNode.narrowerTerms = []; // clear the narrowerTerms
        let thesPaths: ThesaurusPath[] = this.isUseForTerm ? (this.useTermsInstead.paths ?? []) : (this.entry.paths ?? []);
        let i = 1;

        if (thesPaths.length > 1) {
            this.entry.notUnique = true;
        }
        for (let path of thesPaths) {
            console.log("Processing path: " + i);
            i++;
            for (let term of path.terms) {
                term = await firstValueFrom(this.thesaurusService.getthesaurusSingleEntry(thesaurusId, term.termOrdinal, token));
                term.narrowerTerms = [];
                this.addTermToTree(term, path);
            }
            if (this.isUseForTerm) {
                this.addTermToTree(this.useTermsInstead, path, true);
            }
            else {
                this.addTermToTree(this.entry, path, true);
            }
        }
        return this.rootNode;
    }
    
    /**
     * Inserts a term into the tree. If the term's broader term is the root, it is
     * added directly to root's children; otherwise it is placed via `addToLeafNode`.
     * @param isEntryNode When true, a new entry object is created to support multi-path display.
     */
    addTermToTree(term: ThesaurusEntry, path: ThesaurusPath, isEntryNode?: boolean) : void {
        if (term.broaderTerms && term.broaderTerms.length === 1 && term.broaderTerms[0].termOrdinal === this.rootNode.termOrdinal) {
            term.paths = ((term.paths??[]).length > 1 ? term.paths : [path]);
            if (!this.rootNode.narrowerTerms) 
                this.rootNode.narrowerTerms = [];
            if (!this.rootNode.narrowerTerms.some(nt => nt.termOrdinal === term.termOrdinal)) {
                this.rootNode.narrowerTerms.push(term);
            }
            console.log("Added to root: " + term.name + " via path " + path.tn);
            //return true;
        }
        else {
           this.addToLeafNode(term, path, this.rootNode,isEntryNode);          
        }
    }

    /**
     * Recursively searches the subtree rooted at `rootNode` for the parent of `term`
     * and appends `term` as a child when found.
     * @returns `true` if the term was successfully inserted, `false` otherwise.
     */
    private addToLeafNode(term: ThesaurusEntry, path: ThesaurusPath, rootNode: ThesaurusEntry, isEntryNode?: boolean): boolean {
    // Exit early if term lacks broader terms or root node has no narrower terms
    if (!term.broaderTerms || !rootNode.narrowerTerms) return false;

    // Iterate through all broader terms of the current term
    for (const bt of term.broaderTerms) {
        // Search among children of the current root node
        for (const t of rootNode.narrowerTerms) {
            // Check if current child matches the broader term and shares the same thesaurus
            if (t.termOrdinal === bt.termOrdinal && (t.tn == term.tn || this.comparePathTerms(t.paths?.[0], term.paths?.[0]))) {
                // Ensure the child has a narrowerTerms array initialized
                if (!t.narrowerTerms) t.narrowerTerms = [];
                // Check if term is already present among this node's children to avoid duplicates
                const alreadyPresent = t.narrowerTerms.some(nt => nt.termOrdinal === term.termOrdinal);
                if (!alreadyPresent) {
                  // Handle regular term insertion
                  if (!isEntryNode) {
                    // Update paths if term has multiple paths; otherwise keep existing paths
                    term.paths = ((term.paths??[]).length > 1 ? this.getCorrectPath(path, term.paths??[]) : term.paths);
                    t.narrowerTerms.push(term);
                    console.log("Added: " + term.name + " under " + t.name + " via path " + path.tn);
                    return true;
                  }
                  // Handle entry node creation for multi-path display
                  else{
                    t.narrowerTerms.push(this.createNewEntry(term, path));
                    console.log("Added new Entry Node: " + term.name + " under " + t.name + " via path " + path.tn);
                    return true;
                  }
                }
                else{
                    // Term already exists as a child of this node in this path
                    console.log("Term already present: " + term.name + " under " + t.name + " via path " + path.tn);
                }
            }
            // Continue searching deeper in the tree for the parent node
            if (this.addToLeafNode(term, path, t, isEntryNode)) {
                return true;
            }
        }
    }
    // Term was not inserted at this level or any deeper level
    return false;
  }

  /**
   * Creates a shallow copy of `term` scoped to the given `path`, filtering its
   * narrower terms accordingly, and registers it in `entryObjects`.
   */
  createNewEntry(term: ThesaurusEntry, path: ThesaurusPath): ThesaurusEntry {
  let entryObject: ThesaurusEntry = {
                        termOrdinal: term.termOrdinal,
                        name: term.name,
                        description: term.description,
                        broaderTerms: term.broaderTerms,
                        narrowerTerms: this.filterNarrowerTermsByPath(term.narrowerTerms ?? [], path.tn),
                        relatedTerms: term.relatedTerms,
                        useTermFor: term.useTermFor,
                        paths: [path],
                        id: term.id,
                        thesaurusId: term.thesaurusId,
                        qualifiers: term.qualifiers,
                        historyNote: term.historyNote,
                        creationYear: term.creationYear,
                        registry: term.registry,
                        alt: term.alt,
                        notUnique: term.notUnique
                    };
                    this.entryObjects.push(entryObject);
                return entryObject;
                }
  
  /** Returns `true` if `node` represents the primary entry (or its use-for term) that should be highlighted in the tree. */
  isDisplayTerm(node: ThesaurusEntry): boolean {
        if (this.isUseForTerm) {
            return node.termOrdinal === this.useTermsInstead.termOrdinal;
        }
        return node.termOrdinal === this.entry.termOrdinal;
    }

    isRootNode = (node: ThesaurusEntry) => node?.termOrdinal === this.rootNode.termOrdinal;

    hasChild = (_: number, node: ThesaurusEntry) => !!node?.narrowerTerms && node?.narrowerTerms.length > 0;

    /** Tree predicate: returns `true` when `node` has no children. */
    isLeaf = (_: number, node: ThesaurusEntry) => !this.hasChild(_, node);

    /** Returns `true` when `node`'s children have already been fetched from the API (broaderTerms present on first child). */
    isLoaded = (node: ThesaurusEntry) => !!node?.narrowerTerms && node?.narrowerTerms.length > 0 && !!node?.narrowerTerms[0]?.broaderTerms;

    /**
     * Called when a tree node is expanded. If the node's children have not yet
     * been loaded from the API, fetches them and refreshes the tree.
     */
    async handleNodeChange(node: ThesaurusEntry) {
        if (!this.isLoaded(node)) {
            this.thesTree.collapse(node);
            this.showSpinner(node);
            const children = await this.populateNarrowerterms(node, this.thesaurus ? this.thesaurus.id : '', this.token);
            node.narrowerTerms = children;
            this.refreshTree(node);
        }
    }

    /**
     * Forces the Material tree to re-render by resetting the data source, then
     * re-expands the given node and hides its loading spinner.
     */
    refreshTree(node: ThesaurusEntry) {
        let data = this.thesaurusDataSource.data;
        this.thesaurusDataSource.data = [];
        this.thesaurusDataSource.data = data;
        this.hideSpinner(node);
        this.thesTree.expand(node);
    }

  /**
   * Fetches full details for each narrower term of `entry`, selects the correct
   * path when a term belongs to multiple paths, and deduplicates results.
   * @returns A list of fully populated child `ThesaurusEntry` objects.
   */
  async populateNarrowerterms(entry: ThesaurusEntry, thesaurusId: string, token: string): Promise<ThesaurusEntry[]> {
    let entryNT : ThesaurusEntry[] = [];
    entry.narrowerTerms = this.filterNarrowerTermsByPath(entry.narrowerTerms ?? [], entry.tn ?? '');
    if (entry.narrowerTerms) {
      for (let term of entry.narrowerTerms) {
        await firstValueFrom(this.thesaurusService.getthesaurusSingleEntry(thesaurusId, term.termOrdinal, token)).then(
          narrowerTerm => {
            if (narrowerTerm.paths && narrowerTerm.paths.length > 1) {
              let selectedPath : ThesaurusPath = {
                tn: "",
                terms: []
              };
              for (let p of narrowerTerm.paths) {
                if (entry.paths?.[0] && p.tn) {
                    if (p.tn.indexOf(entry.paths[0].tn) === 0) {
                        selectedPath = p;
                        break;
                    }
                } else if ( this.comparePathTerms(entry.paths?.[0], p) ) {
                    selectedPath = p;
                    break;
                }
              }
              narrowerTerm.paths = [selectedPath];
              narrowerTerm.narrowerTerms = this.filterNarrowerTermsByPath(narrowerTerm.narrowerTerms ?? [], narrowerTerm.paths?.[0]?.tn ?? '');

            }
            if (entryNT.findIndex(e => e.termOrdinal === narrowerTerm.termOrdinal) === -1) {
              entryNT.push(narrowerTerm);
              this.hasNotes = this.hasNotes || this.checkIfEntryHasNotes(narrowerTerm);
            }
      });
      }
    }
    return entryNT;
  }

  /**
   * Filters `terms` to only those whose `tn` (term-node identifier) matches the
   * given path string. Returns the full list when `tn` is empty.
   */
  filterNarrowerTermsByPath(terms: ThesaurusEntry[], tn: string): ThesaurusEntry[] {
    if (tn) {
      return terms.filter(term => {
        return term.tn === tn;
      });
    }
    return terms;
  }

  /** Returns `true` if `entry` has a description, use-for terms, or related terms to display in the notes panel. */
  checkIfEntryHasNotes(entry: ThesaurusEntry): boolean {
    return !!entry.description || !!entry.useTermFor?.length || !!entry.relatedTerms?.length;
  }

    /**
     * Returns the DOM element ID for a tree node, assigning the special
     * `'finalTermEntryLabel'` ID to the primary entry node.
     */
    getId(node: ThesaurusEntry): string {
        if (node.termOrdinal === this.entry.termOrdinal) {
            return 'finalTermEntryLabel';
        }
        return node.id;
    }

    /** Hides the expand button for the node identified by `ordinal` and emits an event to show its inline spinner. */
    showSpinner(node: ThesaurusEntry) {
      let nodeButton = document.getElementById("mat-tree-node-" + node.termOrdinal + (node.paths?.[0] ? this.utilitiesService.getPathIdentifier(node.paths) : ""));
      nodeButton?.classList.add("hide-node-button");
      this.eventsService.emitShowEntrySpinner(node.termOrdinal + (node.paths?.[0] ? this.utilitiesService.getPathIdentifier(node.paths) : ""));
   }

   /** Restores the expand button for the node identified by `ordinal` and emits an event to hide its inline spinner. */
   hideSpinner(node: ThesaurusEntry) {
      let nodeButton = document.getElementById("mat-tree-node-" + node.termOrdinal + (node.paths?.[0] ? this.utilitiesService.getPathIdentifier(node.paths) : ""));
      nodeButton?.classList.remove("hide-node-button");
      this.eventsService.emitHideEntrySpinner(node.termOrdinal + (node.paths?.[0] ? this.utilitiesService.getPathIdentifier(node.paths) : ""));
   }
   
  /** Shows the full-page index spinner and disables the help button while the tree is loading. */
  showIndexSpinner() {
    this.eventsService.emitShowIndexSpinner();
    this.eventsService.emitDisableHelpButtonEvent(true);
  }

  /** Hides the full-page index spinner and re-enables the help button once the tree has loaded. */
  hideIndexSpinner() {
    this.eventsService.emitHideIndexSpinner();
    this.eventsService.emitDisableHelpButtonEvent(false);
  }

  /**
   * Returns `true` if both paths contain exactly the same terms (matched by `termOrdinal`),
   * regardless of order.
   */
  comparePathTerms(path1: ThesaurusPath | undefined, path2: ThesaurusPath | undefined): boolean {
    if (!path1 || !path2) {
        return false;
    }
    if (path1.terms.length !== path2.terms.length - 1) {
        return false;
    }
    for (let i = 0; i < path1.terms.length; i++) {
        if (path1.terms[i].termOrdinal === path2.terms[i].termOrdinal) {
            continue;
        }
        else 
            return false;
    }
    return true;
  }

  /**
   * From a list of candidate paths, returns only those whose `tn` is a prefix of
   * the current `path.tn`, ensuring the term is placed on the correct branch.
   */
  getCorrectPath(path: ThesaurusPath, paths: ThesaurusPath[]): ThesaurusPath[] {
    if (paths.length > 1) {
        if (path.tn) {
            paths = paths.filter(p => path.tn.indexOf(p.tn) === 0);
        }
        else {
            // Select the path with the most matching terms as the best match
            paths = [paths.reduce((best, current) => {
                const currentMatchCount = current.terms.filter(t => 
                    path.terms.some(pt => pt.termOrdinal === t.termOrdinal)
                ).length;
                const bestMatchCount = best.terms.filter(t => 
                    path.terms.some(pt => pt.termOrdinal === t.termOrdinal)
                ).length;
                return currentMatchCount > bestMatchCount ? current : best;
            })];
        }
    }
    return paths;
  }

}
